/**
 * ReacceptTermsGate — blocks the app when /config/public.terms_version
 * is greater than the tenant's last-accepted version.
 *
 * Per product direction: adults see a blocking modal with a
 * plain-language "What's new in this update" summary above the doc
 * links + "I accept" button; kids see a friendly "show a grown-up"
 * interstitial (unchanged — never sees the summary). Acceptance writes
 * via /billing/accept_terms (which does NOT touch consent — that path
 * is the Consent Gate's job).
 *
 * The blocker stays blocking: no close button, no scrim tap-out, no
 * Escape key. The only way forward is "I accept" (or closing the app).
 *
 * The summary content comes from ``terms_release_notes.json`` keyed by
 * the stringified current_terms_version; a missing entry falls back to
 * a single calm bullet so the section is never empty (an absent
 * section reads more alarming than a small bullet).
 *
 * For accounts with no connected kids the parent/guardian footnote is
 * dropped — solo adults and teens see the same summary but without the
 * "you're the parent or guardian" framing.
 */

import { useEffect, useState } from "react";
import { auth as authApi } from "../api";
import { useBilling } from "../billing/useBilling";
import { useGatePresence } from "../safety/gateStack";
import { useLifecycle } from "./useLifecycle";
import { WhatsNew, type TermsReleaseEntry } from "./WhatsNew";
import releaseNotes from "./terms_release_notes.json";

interface Props {
  onViewDoc?: (doc: "terms" | "privacy") => void;
}

/** Pull the per-release entry for the active terms version, if any. The
 *  JSON is keyed by the EXACT string in current_terms_version (the
 *  integer the backend returns is stringified at lookup). A missing
 *  entry is fine — ``<WhatsNew/>`` falls back to a calm one-bullet
 *  line. */
function lookupRelease(version: number | null): TermsReleaseEntry | undefined {
  if (version == null) return undefined;
  const notes = releaseNotes as Record<string, TermsReleaseEntry | string>;
  const entry = notes[String(version)];
  if (!entry || typeof entry === "string") return undefined;
  return entry;
}

export function ReacceptTermsGate({ onViewDoc }: Props): JSX.Element | null {
  const lc = useLifecycle();
  const b = useBilling();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Family shape — used only to decide whether the guardian footnote
  // applies. Solo adults / teens with no connected kids see the same
  // summary but without the parent/guardian framing. ``null`` while
  // ``/auth/me`` is in flight: we render the footnote-less variant
  // (safer default — no claim about a guardian relationship that may
  // not exist).
  const [hasKids, setHasKids] = useState<boolean | null>(null);

  // Register with the global gate stack so social/friend visibility
  // drops while the reaccept blocker is up.
  const gateOpen = lc.needsReaccept && b.isReady;
  useGatePresence(gateOpen);

  // Only resolve ``has_children`` once we know the gate will render and
  // the principal is an adult. Kids never see the summary or the
  // footnote, so the lookup is wasted there.
  const isKid = b.kind === "kid";
  useEffect(() => {
    if (!gateOpen || isKid) return;
    let cancelled = false;
    authApi
      .me()
      .then((m) => {
        if (!cancelled) setHasKids(Boolean(m.has_children));
      })
      .catch(() => {
        if (!cancelled) setHasKids(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gateOpen, isKid]);

  // Reset the pending/error state if the gate is re-shown for a NEWER
  // version (an admin bump while the user is on the page).
  useEffect(() => {
    setPending(false);
    setError(null);
  }, [lc.currentTermsVersion]);

  if (!lc.needsReaccept) return null;
  if (!b.isReady) return null;

  const release = lookupRelease(lc.currentTermsVersion);

  const handleAccept = async () => {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await lc.acceptCurrent();
      // ``useLifecycle.acceptCurrent`` refreshes billing.terms_version,
      // which clears ``needsReaccept`` and unmounts this gate.
    } catch {
      setPending(false);
      setError("We couldn't save that. Please try again.");
    }
  };

  return (
    <div role="dialog" aria-modal="true" className="wbm-reaccept-gate">
      <div className="wbm-reaccept-gate__card">
        {isKid ? (
          <>
            <h2>Show this to a grown-up</h2>
            <p>
              Some grown-up stuff in world by me changed. A grown-up needs
              to read the new bits before you can keep going.
            </p>
            <p className="wbm-reaccept-gate__kid-note">
              Tell them: "world by me's grown-up rules changed — open
              the app on your phone and tap okay."
            </p>
          </>
        ) : (
          <>
            <h2>We've updated our Terms</h2>
            <p>
              Here's a quick summary of what changed. Have a read, then
              accept to keep using world by me.
            </p>

            {/* The new section — slotted between the headline and the
             *  full-document links. ``<WhatsNew/>`` is defensive: it
             *  renders a calm one-bullet fallback when ``release`` is
             *  undefined so the section is never empty. */}
            <WhatsNew release={release} />

            <p>
              <button
                type="button"
                className="wbm-reaccept-gate__link"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onViewDoc?.("terms");
                }}
              >
                Read the Terms
              </button>{" "}
              ·{" "}
              <button
                type="button"
                className="wbm-reaccept-gate__link"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onViewDoc?.("privacy");
                }}
              >
                Read the Privacy Policy
              </button>
            </p>
            <button
              type="button"
              className="wbm-reaccept-gate__primary"
              disabled={pending}
              onClick={handleAccept}
            >
              {pending ? "Saving…" : "I accept the updated Terms"}
            </button>
            {error && (
              <p
                role="status"
                style={{
                  fontSize: 13,
                  color: "#98664d",
                  margin: "10px 0 0",
                  fontWeight: 700,
                }}
              >
                {error}
              </p>
            )}
            {hasKids && (
              <p
                style={{
                  fontSize: 12,
                  color: "#6b7580",
                  lineHeight: 1.45,
                  margin: "10px auto 0",
                  maxWidth: 360,
                  textAlign: "center",
                }}
              >
                Accepting confirms you're the parent or guardian on this
                account.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
